import React, { useCallback, useMemo, useRef, useState } from "react";
import {
  GestureResponderHandlers,
  LayoutChangeEvent,
  PanResponder,
} from "react-native";
import { Box } from "../../ui/box";
import { HStack } from "../../ui/hstack";
import { Text } from "../../ui/text";
import { VStack } from "../../ui/vstack";

/** A zone of the scale with its own step size. Zones are applied in order, each
 *  covering `(previous.until, until]`. With segments the track is laid out by
 *  equal tick width — every selectable value gets the same slice of the track —
 *  so dense low-end ranges (e.g. 0–10k followers) are no longer cramped. */
export interface StepSegment {
  /** Upper bound (inclusive) of this zone. */
  until: number;
  /** Step size within this zone. */
  step: number;
}

/** Follower preset (0–1M): fine near zero, coarse at the top, so picking 5k is
 *  as easy as picking 500k. */
export const FOLLOWER_STEP_SEGMENTS: StepSegment[] = [
  { until: 10_000, step: 1_000 },
  { until: 100_000, step: 5_000 },
  { until: 1_000_000, step: 50_000 },
];

/** Budget preset (0–2M): fine near zero so small budgets are easy to pick,
 *  coarse at the top. */
export const BUDGET_STEP_SEGMENTS: StepSegment[] = [
  { until: 100_000, step: 5_000 },
  { until: 500_000, step: 50_000 },
  { until: 1_000_000, step: 100_000 },
];

interface RangeSliderProps {
  label: string;
  min: number;
  max: number;
  /** Fixed step (linear scale). Ignored when `steps` is provided. */
  step?: number;
  /** Per-zone steps (piecewise scale). Takes precedence over `step`. */
  steps?: StepSegment[];
  /** [lower, upper] selected bounds. */
  value: [number, number];
  onChange: (value: [number, number]) => void;
  /** Formats a value for the min/max labels. */
  format?: (n: number) => string;
}

/** 0 = lower thumb, 1 = upper thumb. */
type ThumbIndex = 0 | 1;

const THUMB_SIZE = 18;

const clamp = (n: number, lo: number, hi: number) =>
  Math.max(lo, Math.min(hi, n));

/** Builds the ascending list of selectable values from the step segments. */
const buildTicks = (
  min: number,
  max: number,
  segments: StepSegment[],
): number[] => {
  const ticks: number[] = [min];
  let current = min;
  for (const seg of segments) {
    if (!(seg.step > 0)) continue;
    const end = Math.min(seg.until, max);
    while (current < end) {
      current = Math.min(current + seg.step, end);
      ticks.push(current);
    }
    if (current >= max) break;
  }
  if (ticks[ticks.length - 1] < max) ticks.push(max);
  return ticks;
};

/** Index of the tick closest to `v` (binary search over the sorted ticks). */
const nearestIndex = (ticks: number[], v: number): number => {
  if (v <= ticks[0]) return 0;
  const last = ticks.length - 1;
  if (v >= ticks[last]) return last;
  let lo = 0;
  let hi = last;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (ticks[mid] < v) lo = mid + 1;
    else hi = mid;
  }
  return Math.abs(ticks[lo] - v) < Math.abs(ticks[lo - 1] - v) ? lo : lo - 1;
};

/** A single draggable knob, positioned by its centre at `x` px from the track's
 *  left edge. Presentational only — drag handling lives in the parent. */
function Thumb({
  x,
  label,
  handlers,
}: {
  x: number;
  label: string;
  handlers: GestureResponderHandlers;
}) {
  return (
    <Box
      {...handlers}
      accessibilityRole="adjustable"
      accessibilityLabel={label}
      className="absolute rounded-full bg-background-0 border-2 border-brand-500 cursor-pointer"
      style={{
        left: x - THUMB_SIZE / 2,
        width: THUMB_SIZE,
        height: THUMB_SIZE,
      }}
    />
  );
}

/**
 * Two-thumb range slider. Drag either knob; the lower knob can't cross the
 * upper one and vice-versa. Works on web and native via PanResponder.
 *
 * Scale: linear by default (`step`), or piecewise when `steps` is given — each
 * zone then occupies equal track space per selectable value, which is what makes
 * the low end easy to hit on wide ranges (e.g. followers 0–1M).
 *
 * A PanResponder is created once, so its callbacks can't close over props
 * directly (they'd capture the first render's values). Instead every render
 * mirrors the current props (and the scale mappers) into `latest`, and the
 * callbacks read from there — this is what keeps the *other* thumb in place
 * while you drag one of them.
 */
const RangeSlider = React.memo(function RangeSlider({
  label,
  min,
  max,
  step = 1,
  steps,
  value,
  onChange,
  format = (n) => String(n),
}: RangeSliderProps) {
  const range = max - min || 1;
  const [trackWidth, setTrackWidth] = useState(0);

  const ticks = useMemo(
    () => (steps && steps.length ? buildTicks(min, max, steps) : null),
    [steps, min, max],
  );

  // Scale mappers — value ⇄ normalised track position (0..1). Piecewise mode
  // maps through equal-width ticks; linear mode snaps to a fixed step.
  const valueToPos = useCallback(
    (v: number): number => {
      if (ticks) {
        return ticks.length > 1
          ? nearestIndex(ticks, v) / (ticks.length - 1)
          : 0;
      }
      return (v - min) / range;
    },
    [ticks, min, range],
  );
  const posToValue = useCallback(
    (pos: number): number => {
      const p = clamp(pos, 0, 1);
      if (ticks) {
        const idx = clamp(
          Math.round(p * (ticks.length - 1)),
          0,
          ticks.length - 1,
        );
        return ticks[idx];
      }
      return clamp(Math.round((min + p * range) / step) * step, min, max);
    },
    [ticks, min, max, range, step],
  );

  // Always-current snapshot for the (stable) PanResponder callbacks.
  const latest = useRef({
    value,
    trackWidth,
    min,
    max,
    valueToPos,
    posToValue,
    onChange,
  });
  latest.current = {
    value,
    trackWidth,
    min,
    max,
    valueToPos,
    posToValue,
    onChange,
  };

  // The thumb's normalised position when the active drag started.
  const dragStartPos = useRef(0);

  const onTrackLayout = useCallback((e: LayoutChangeEvent) => {
    setTrackWidth(e.nativeEvent.layout.width);
  }, []);

  const createThumbResponder = useCallback(
    (index: ThumbIndex) =>
      PanResponder.create({
        onStartShouldSetPanResponder: () => true,
        onMoveShouldSetPanResponder: () => true,
        onPanResponderTerminationRequest: () => false,
        onPanResponderGrant: () => {
          dragStartPos.current = latest.current.valueToPos(
            latest.current.value[index],
          );
        },
        onPanResponderMove: (_e, gesture) => {
          const s = latest.current;
          if (!s.trackWidth) return;

          // Drag distance (px) → normalised position → snapped value.
          const pos = dragStartPos.current + gesture.dx / s.trackWidth;
          const snapped = s.posToValue(pos);

          const [lower, upper] = s.value;
          const next: [number, number] =
            index === 0
              ? [clamp(snapped, s.min, upper), upper] // lower stops at upper
              : [lower, clamp(snapped, lower, s.max)]; // upper stops at lower

          if (next[0] !== lower || next[1] !== upper) s.onChange(next);
        },
      }),
    [],
  );

  const lowerResponder = useMemo(
    () => createThumbResponder(0),
    [createThumbResponder],
  );
  const upperResponder = useMemo(
    () => createThumbResponder(1),
    [createThumbResponder],
  );

  // Value → px from the track's left edge.
  const lowerX = valueToPos(value[0]) * trackWidth;
  const upperX = valueToPos(value[1]) * trackWidth;

  return (
    <VStack space="xs">
      <HStack className="items-center justify-between">
        <Text size="sm" bold className="text-typography-800">
          {label}
        </Text>
        <Text size="xs" className="text-typography-500 tabular-nums">
          {format(value[0])} – {format(value[1])}
        </Text>
      </HStack>

      <Box className="h-5 justify-center" onLayout={onTrackLayout}>
        {/* track */}
        <Box className="h-1.5 rounded-full bg-background-200" />
        {/* selected segment */}
        <Box
          className="absolute h-1.5 rounded-full bg-brand-500"
          style={{ left: lowerX, width: Math.max(0, upperX - lowerX) }}
        />
        <Thumb
          x={lowerX}
          label={`${label}: mínimo`}
          handlers={lowerResponder.panHandlers}
        />
        <Thumb
          x={upperX}
          label={`${label}: máximo`}
          handlers={upperResponder.panHandlers}
        />
      </Box>
    </VStack>
  );
});

RangeSlider.displayName = "RangeSlider";

export default RangeSlider;
